import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------
// driver.js ships unstyled-ish defaults; this overrides them to match the app.
//
// driver.js sets z-index inline on the overlay SVG (10000) and the popover
// (1000000000). We restack both here: above the app's schedule drawers and
// sheet overlays (2001-2005), and below the MUI tooltip layer (99999, set in
// theme/index.js) so tooltips stay readable mid-step.
//
// The base sits above driver's own inline 10000 so that the highlighted
// element, lifted to TOUR_Z_INDEX + 1, reliably clears the overlay -- without
// that margin, form controls inside it stay unclickable.
// ----------------------------------------------------------------------

export const TOUR_Z_INDEX = 10050;

export function tourOverlayColor(theme) {
  const isLight = theme.palette.mode === 'light';
  return alpha(theme.palette.grey[900], isLight ? 0.55 : 0.72);
}

export default function buildTourStyles(theme) {
  const isLight = theme.palette.mode === 'light';

  const surface = isLight ? '#FFFFFF' : theme.palette.grey[800];
  const titleColor = isLight ? theme.palette.grey[900] : theme.palette.grey[100];
  const bodyColor = isLight ? theme.palette.grey[600] : theme.palette.grey[400];
  const brand = theme.palette.primary.main;
  const brandDark = theme.palette.primary.dark;
  const brandLight = theme.palette.primary.lighter;

  return `
    /* driver.js sets the overlay's fill/opacity inline from the overlayColor and
       overlayOpacity options, so those are configured in TourProvider, not here.
       Only the stacking order needs forcing. */
    .driver-overlay { z-index: ${TOUR_Z_INDEX} !important; }
    /* Above the lifted portals below, so the popover is never hidden behind a
       dialog or dropdown it is pointing at. */
    .driver-popover { z-index: ${TOUR_Z_INDEX + 20} !important; }

    /* The highlighted element needs to sit above the overlay to stay usable.
       z-index only applies to positioned elements, and driver.js sets none --
       so give it one here, or inputs inside it cannot be clicked. */
    .driver-active-element {
      position: relative !important;
      z-index: ${TOUR_Z_INDEX + 1} !important;
    }

    /* Autocomplete, Select and Menu render their dropdowns in a portal on
       document.body -- outside the highlighted element, so they fall under the
       overlay and cannot be clicked. Lift and re-enable them while a tour runs.
       Enlarging the highlighted area cannot fix this: the popup is not inside
       it in the DOM at all. */
    .driver-active .MuiAutocomplete-popper,
    .driver-active .MuiPopper-root,
    .driver-active .MuiPopover-root,
    .driver-active .MuiMenu-root,
    .driver-active .MuiModal-root:not(.MuiDialog-root) {
      z-index: ${TOUR_Z_INDEX + 3} !important;
      pointer-events: auto !important;
    }

    /* A dialog is a container, not a popup: the highlighted element lives
       inside it. It is lifted so that child can clear the overlay, but its own
       surface stays inert -- everything in the dialog except the highlighted
       element is dimmed area, and dimmed area does not respond to clicks. */
    .driver-active .MuiDialog-root {
      z-index: ${TOUR_Z_INDEX + 3} !important;
      pointer-events: none !important;
    }

    /* The dialog's own backdrop sits behind the dialog, so it dims the page
       but never the dialog itself. It is dimmed area, so -- like the tour
       overlay -- clicking it does nothing while a guide is running, rather
       than dismissing the dialog out from under the user. */
    .driver-active .MuiDialog-root > .MuiBackdrop-root {
      opacity: 0.35 !important;
      pointer-events: none !important;
    }

    /* The tour overlay cuts a hole around the highlighted element, but it is
       drawn beneath the dialog, which has to be lifted above it so that
       element stays usable. The dialog's own surface therefore never gets
       dimmed, leaving this stage looking lit up where every other stage reads
       as disabled. Lay the same veil over the dialog surface itself, so the
       inside of the window dims exactly like the page outside it. */
    .driver-active .MuiDialog-root .MuiDialog-paper {
      position: relative;
    }
    .driver-active .MuiDialog-root .MuiDialog-paper::after {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 1;
      border-radius: inherit;
      background: ${tourOverlayColor(theme)};
      pointer-events: none;
    }

    /* A highlighted element inside a lifted portal has to clear that portal,
       not just the overlay -- and, in a dialog, the veil above as well, so it
       alone stays bright.

       The isolation property is what makes that reliable. The veil and the highlighted
       element are both descendants of the dialog paper, and the element's
       contents -- an outlined input's own box, a table's rows and sticky
       headers -- build stacking contexts of their own. Without isolation those
       resolve against the paper, so the veil paints over parts of the element
       even though the element itself is lifted: the inside of a field stays
       grey, or a list lights up only on its first row. Isolating the element
       puts it and everything inside it in one layer that clears the veil
       whole.

       The background matters too: a transparent element lets the veil show
       through from behind, which looks the same as not being lifted at all.
       Give it the dialog's own surface colour so it reads as lit. */
    .driver-active .MuiDialog-root .driver-active-element,
    .driver-active .MuiModal-root .driver-active-element {
      z-index: ${TOUR_Z_INDEX + 4} !important;
      isolation: isolate;
      background-color: ${surface};
      border-radius: 4px;
    }

    .driver-active .MuiAutocomplete-popper *,
    .driver-active .MuiPopper-root *,
    .driver-active .MuiPopover-root *,
    .driver-active .MuiMenu-root *,
    .driver-active .MuiModal-root:not(.MuiDialog-root) * {
      pointer-events: auto !important;
    }

    /* Only the highlighted element inside a dialog takes clicks again. */
    .driver-active .MuiDialog-root .driver-active-element,
    .driver-active .MuiDialog-root .driver-active-element * {
      pointer-events: auto !important;
    }

    /* MUI inputs build their own stacking contexts, which can leave the field
       itself behind the overlay even when its wrapper is lifted. Lift the
       inputs inside the highlighted area too.
       Switches and checkboxes are excluded: they rely on absolutely positioned
       internals, which repositioning breaks. */
    .driver-active-element input:not([type='checkbox']),
    .driver-active-element textarea,
    .driver-active-element select,
    .driver-active-element .MuiSelect-select,
    .driver-active-element .MuiNativeSelect-select,
    .driver-active-element .MuiAutocomplete-root,
    .driver-active-element .MuiInputBase-root {
      position: relative !important;
      z-index: ${TOUR_Z_INDEX + 1} !important;
    }

    .em-tour-popover.driver-popover {
      max-width: 360px;
      padding: 20px;
      border-radius: 12px;
      background: ${surface};
      box-shadow: ${theme.shadows[16] || '0 8px 24px rgba(22,28,36,.14)'};
      font-family: ${theme.typography.fontFamily};
    }

    .em-tour-popover .driver-popover-arrow { border-color: ${surface}; }

    .em-tour-popover .driver-popover-title {
      /* Clear of the Exit control, which is absolutely positioned top-right. */
      padding-right: 72px;
      margin: 0 0 8px;
      font-size: 18px;
      font-weight: 600;
      line-height: 1.4;
      color: ${titleColor};
    }

    .em-tour-popover .driver-popover-description {
      margin: 0;
      font-size: 14px;
      line-height: 1.6;
      color: ${bodyColor};
    }

    /* Tip strip, rendered into the description by TourProvider. */
    .em-tour-tip {
      display: block;
      margin-top: 12px;
      padding: 10px 12px;
      border-left: 3px solid ${brand};
      border-radius: 6px;
      background: ${alpha(brandLight, isLight ? 0.35 : 0.14)};
      font-size: 13px;
      line-height: 1.55;
      color: ${titleColor};
    }

    .em-tour-popover .driver-popover-progress-text {
      font-size: 12px;
      font-weight: 500;
      color: ${bodyColor};
    }

    .em-tour-popover .driver-popover-footer { margin-top: 18px; gap: 8px; }

    .em-tour-popover .driver-popover-navigation-btns button,
    .em-tour-popover .driver-popover-footer button {
      padding: 7px 16px;
      border: none;
      border-radius: 8px;
      font-family: inherit;
      font-size: 14px;
      font-weight: 600;
      text-shadow: none;
      cursor: pointer;
    }

    .em-tour-popover .driver-popover-next-btn {
      background: ${brand};
      color: ${theme.palette.primary.contrastText};
    }
    .em-tour-popover .driver-popover-next-btn:hover { background: ${brandDark}; }

    .em-tour-popover .driver-popover-prev-btn {
      background: transparent;
      color: ${bodyColor};
    }
    .em-tour-popover .driver-popover-prev-btn:hover { background: ${alpha(
      theme.palette.grey[500],
      0.12
    )}; }

    /* The close control, labelled rather than a bare x. driver.js positions it
       absolutely top-right and sizes it for an icon, so it needs widening to
       fit the text. No fill until hover. */
    .em-tour-popover .driver-popover-close-btn.em-tour-exit {
      width: auto;
      height: auto;
      padding: 10px 12px;
      border: none;
      border-radius: 6px;
      background-color: transparent;
      color: ${bodyColor};
      font-family: inherit;
      font-size: 12px;
      font-weight: 500;
      line-height: 1;
      cursor: pointer;
      transition: background-color 0.15s, color 0.15s;
    }
    .em-tour-popover .driver-popover-close-btn.em-tour-exit:hover {
      background-color: ${alpha(theme.palette.grey[500], 0.12)};
      color: ${titleColor};
    }
    .em-tour-popover .driver-popover-close-btn.em-tour-exit:focus-visible {
      outline: 2px solid ${brand};
      outline-offset: 2px;
    }

    @media (prefers-reduced-motion: reduce) {
      .driver-popover, .driver-overlay { transition: none !important; }
    }
  `;
}
